import React from 'react';
import { FaPills, FaFlask, FaCapsules, FaLeaf, FaVial, FaIndustry } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const ManufacturingPage = () => {
  const capabilities = [
    {
      icon: FaPills,
      title: 'Tablets',
      description: 'Oval, Round, Chewable & Effervescent tablets',
      capacity: '75,000+ tablets per batch',
      features: ['85+ coating colors', 'Chewable tablets', 'Effervescent tablets', 'Sugar-free options'],
      image: 'https://images.unsplash.com/photo-1641561421178-db8542057811?w=400&q=80'
    },
    {
      icon: FaCapsules,
      title: 'Capsules',
      description: 'All capsule sizes (Size 000 to Size 1)',
      capacity: '60,000+ capsules per batch',
      features: ['Hard gelatin capsules', 'Vegetarian capsules', 'Multiple colors'],
      image: 'https://images.unsplash.com/photo-1623834928030-2208b3081255?w=400&q=80'
    },
    {
      icon: FaFlask,
      title: 'Syrups & Juices',
      description: 'Herbal syrups, tonics, and juices',
      capacity: '2,500+ bottles per batch',
      features: ['Sugar-free options', 'Multiple flavors', 'Various bottle shapes'],
      image: 'https://images.unsplash.com/photo-1671493234884-b1611bcf3e69?w=400&q=80'
    },
    {
      icon: FaLeaf,
      title: 'Herbal Oils',
      description: 'Sesame-based and other medicated oils',
      capacity: '3,000+ bottles per batch',
      features: ['Traditional formulations', 'Custom blends', 'Multiple packaging'],
      image: 'https://images.unsplash.com/photo-1671493235081-5842463637cd?w=400&q=80'
    },
    {
      icon: FaVial,
      title: 'Powders & Sachets',
      description: 'Protein powders, health supplements, instant drink sachets',
      capacity: '200+ kg per batch',
      features: ['Flavored & unflavored', 'Single-serve sachets', 'Custom formulations'],
      image: 'https://images.pexels.com/photos/4439055/pexels-photo-4439055.jpeg?w=400&q=80'
    },
    {
      icon: FaIndustry,
      title: 'Ointments & Cosmetics',
      description: 'Pain relief balms, skin creams, herbal cosmetics',
      capacity: 'Custom batch sizes',
      features: ['Tubes & jars', 'Multiple textures', 'Premium formulations'],
      image: 'https://images.pexels.com/photos/8940748/pexels-photo-8940748.jpeg?w=400&q=80'
    },
    {
      icon: FaIndustry,
      title: 'Herbal Soaps',
      description: 'Beauty soaps, medicated soaps, baby care',
      capacity: 'Custom batch sizes',
      features: ['Premium ingredients', 'Custom fragrances', 'Attractive packaging'],
      image: 'https://images.pexels.com/photos/6621470/pexels-photo-6621470.jpeg?w=400&q=80'
    },
    {
      icon: FaIndustry,
      title: 'Specialized Products',
      description: 'Chyawanprash, Vati, Ark, Asav, Arishta',
      capacity: 'Custom batch sizes',
      features: ['Traditional recipes', 'GMP compliant', 'Quality assured'],
      image: 'https://images.unsplash.com/photo-1722931303388-527993417e23?w=400&q=80'
    },
  ];

  return (
    <div className="min-h-screen" data-testid="manufacturing-page">
      <section className="bg-gradient-to-r from-[#0F5132] to-[#0F5132] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Manufacturing Capabilities</h1>
          <p className="text-xl text-gray-100 max-w-3xl mx-auto">
            State-of-the-art production facility with automated lines for diverse product formats
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {capabilities.map((item, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-300">
                <div className="bg-[#0F5132] p-6 flex items-center justify-center h-48">
                  <img 
                    src={item.image} 
                    alt={item.title}
                    className="w-full h-full object-cover rounded-lg"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <item.icon className="text-[#0F5132] text-2xl" />
                    <h3 className="text-2xl font-bold text-[#0F5132]">{item.title}</h3>
                  </div>
                  <p className="text-gray-600 mb-4">{item.description}</p>
                  <div className="text-xl font-bold text-[#0F5132] mb-4">{item.capacity}</div>
                  <ul className="space-y-2">
                    {item.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center space-x-2 text-sm text-gray-600">
                        <span className="w-1.5 h-1.5 bg-[#0F5132] rounded-full"></span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0F5132] mb-6">Ready to Start Manufacturing?</h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Browse our complete product catalogue or request a custom manufacturing quote
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/products"
              className="px-8 py-4 bg-[#0F5132] hover:bg-[#0D4228] text-white font-semibold rounded-lg transition-colors"
            >
              View Product Catalogue
            </Link>
            <Link
              to="/third-party-manufacturing"
              className="px-8 py-4 bg-[#0F5132] hover:bg-[#C9A227] text-white font-semibold rounded-lg transition-colors"
            >
              Request Manufacturing Quote
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ManufacturingPage;